/**
 * CSV import flow: presign → upload → preview → commit. Renders
 * row-by-row error reports inline so the admin can fix the source
 * spreadsheet and re-upload before any rows commit.
 */

import { useRef, useState } from "react";

import {
  commitCatalogImport,
  presignCatalogImport,
  previewCatalogImport,
} from "@starz/shared/api/sdk.gen";
import type { ImportPreviewResponse } from "@starz/shared/api/types.gen";

interface Props {
  kind: "material" | "labor" | "zip_factor";
  onCommitted: () => void;
}

const KIND_LABEL: Record<Props["kind"], string> = {
  material: "Materials",
  labor: "Labor rates",
  zip_factor: "ZIP factors",
};

const MAX_BYTES = 5 * 1024 * 1024;

export function CsvImporter({ kind, onCommitted }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<"idle" | "uploading" | "previewing" | "committing">("idle");
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    if (file.size > MAX_BYTES) {
      setError(`CSV is too large; please keep under ${Math.round(MAX_BYTES / 1024 / 1024)} MB.`);
      return;
    }
    setStage("uploading");
    const presign = await presignCatalogImport({ kind, bytes: file.size });
    if ("error" in presign) {
      setStage("idle");
      setError(presign.error.error.message);
      return;
    }
    const putResp = await fetch(presign.data.upload_url, {
      method: "PUT",
      headers: { "Content-Type": "text/csv" },
      body: file,
    });
    if (!putResp.ok) {
      setStage("idle");
      setError(`Upload to storage failed (${putResp.status}). Please retry.`);
      return;
    }
    setStage("previewing");
    const previewResp = await previewCatalogImport({
      importId: presign.data.import_id,
      kind,
    });
    setStage("idle");
    if ("error" in previewResp) {
      setError(previewResp.error.error.message);
      return;
    }
    setPreview(previewResp.data);
  }

  async function onConfirm() {
    if (!preview) return;
    setStage("committing");
    setError(null);
    const result = await commitCatalogImport({ importId: preview.import_id });
    setStage("idle");
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
    onCommitted();
  }

  function onCancel() {
    setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  if (preview) {
    return (
      <div className="space-y-4 rounded-xl border border-starz-gold/20 bg-white p-5 shadow-sm">
        <div>
          <h3 className="font-serif text-xl text-starz-navy">Preview — {KIND_LABEL[kind]}</h3>
          <p className="mt-1 text-sm text-warm-gray">
            {preview.parsed_count} valid row{preview.parsed_count === 1 ? "" : "s"}
            {preview.error_count > 0
              ? ` · ${preview.error_count} error${preview.error_count === 1 ? "" : "s"}`
              : ""}
          </p>
        </div>
        {preview.errors.length > 0 && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm">
            <p className="mb-2 font-medium text-rose-900">Fix these issues and re-upload:</p>
            <ul className="space-y-1 text-rose-700">
              {preview.errors.slice(0, 30).map((e, i) => (
                <li key={`${e.row_number}-${e.column}-${i}`}>
                  Row {e.row_number}, column <code>{e.column}</code>: {e.message}
                </li>
              ))}
              {preview.errors.length > 30 && (
                <li className="italic">…and {preview.errors.length - 30} more.</li>
              )}
            </ul>
          </div>
        )}
        {preview.sample_rows.length > 0 && (
          <div className="overflow-x-auto rounded border border-starz-gold/15">
            <table className="min-w-full text-xs">
              <thead className="bg-starz-cream/40 text-left">
                <tr>
                  {Object.keys(preview.sample_rows[0])
                    .slice(0, 6)
                    .map((col) => (
                      <th key={col} className="px-3 py-2 font-medium text-starz-charcoal">
                        {col}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {preview.sample_rows.map((row, i) => (
                  <tr key={i} className="border-t border-starz-gold/10">
                    {Object.keys(preview.sample_rows[0])
                      .slice(0, 6)
                      .map((col) => (
                        <td key={col} className="px-3 py-2 text-warm-gray">
                          {String(row[col] ?? "")}
                        </td>
                      ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-starz-gold/30 bg-white px-4 py-2 text-sm font-medium text-starz-charcoal hover:bg-starz-cream"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={preview.error_count > 0 || stage === "committing"}
            className="rounded-md bg-starz-navy px-5 py-2 text-sm font-semibold text-starz-off-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {stage === "committing"
              ? "Saving…"
              : `Confirm — write ${preview.parsed_count} row${preview.parsed_count === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={stage !== "idle"}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-starz-navy px-5 py-3 text-sm font-semibold uppercase tracking-wide text-starz-off-white transition-opacity hover:bg-starz-navy/90 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {stage === "uploading" || stage === "previewing"
          ? "Processing…"
          : `Import ${KIND_LABEL[kind]} CSV`}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
        className="hidden"
      />
      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}
    </div>
  );
}
